import { DailyMessages, MessagingMetadata } from '@tadpoles/common'
import type { StaticDecode } from 'typebox'
import { Type } from 'typebox'
import { Value } from 'typebox/value'

import type {
    IdentityPersistenceCollectionDefinition,
    PersistenceCollection
} from '@tadpoles/persistence'

const MessagingDailyStorageRecord = Type.Object(
    {
        cacheKey: Type.String({ minLength: 1 }),
        value: Type.Unknown()
    },
    { additionalProperties: false }
)

const MessagingMetadataStorageRecord = Type.Object(
    {
        subjectKey: Type.String({ minLength: 1 }),
        value: Type.Unknown()
    },
    { additionalProperties: false }
)

type MessagingDailyStorageRecord = StaticDecode<typeof MessagingDailyStorageRecord>
type MessagingMetadataStorageRecord = StaticDecode<typeof MessagingMetadataStorageRecord>

type MessagingDailyCollection = Pick<
    PersistenceCollection<MessagingDailyStorageRecord, 'cacheKey'>,
    'bulkPut' | 'findAll' | 'remove'
>
type MessagingMetadataCollection = Pick<
    PersistenceCollection<MessagingMetadataStorageRecord, 'subjectKey'>,
    'bulkPut' | 'findAll' | 'remove'
>

export type MessagingCacheSnapshot = {
    dailyMessages: DailyMessages[]
    metadata: MessagingMetadata[]
    omittedRecordCount: number
}

export interface MessagingStore {
    load(cutoffDate: string): Promise<MessagingCacheSnapshot>
    replace(
        dailyMessages: readonly DailyMessages[],
        metadata: readonly MessagingMetadata[],
        cutoffDate: string
    ): Promise<void>
}

export const MessagingDailyCollectionDefinition: IdentityPersistenceCollectionDefinition<
    typeof MessagingDailyStorageRecord,
    'cacheKey'
> = {
    kind: 'messaging-daily',
    scope: 'user',
    primaryKey: 'cacheKey',
    modelSchema: MessagingDailyStorageRecord
}

export const MessagingMetadataCollectionDefinition: IdentityPersistenceCollectionDefinition<
    typeof MessagingMetadataStorageRecord,
    'subjectKey'
> = {
    kind: 'messaging-metadata',
    scope: 'user',
    primaryKey: 'subjectKey',
    modelSchema: MessagingMetadataStorageRecord
}

export class FamilyMessagingStore implements MessagingStore {
    readonly #dailyCollection: MessagingDailyCollection
    readonly #metadataCollection: MessagingMetadataCollection

    public constructor(
        dailyCollection: MessagingDailyCollection,
        metadataCollection: MessagingMetadataCollection
    ) {
        this.#dailyCollection = dailyCollection
        this.#metadataCollection = metadataCollection
    }

    public async load(cutoffDate: string): Promise<MessagingCacheSnapshot> {
        const [dailyRecords, metadataRecords] = await Promise.all([
            this.#dailyCollection.findAll(),
            this.#metadataCollection.findAll()
        ])
        const dailyMessages: DailyMessages[] = []
        const metadata: MessagingMetadata[] = []
        let omittedRecordCount = 0

        for (const record of dailyRecords) {
            try {
                const value = Value.Decode(DailyMessages, record.value)
                if (
                    record.cacheKey !== FamilyMessagingStore.#dailyKey(value) ||
                    value.date < cutoffDate
                ) {
                    omittedRecordCount += 1
                    continue
                }

                dailyMessages.push(value)
            } catch {
                omittedRecordCount += 1
            }
        }

        for (const record of metadataRecords) {
            try {
                const value = Value.Decode(MessagingMetadata, record.value)
                if (record.subjectKey !== value.subjectKey) {
                    omittedRecordCount += 1
                    continue
                }

                metadata.push(value)
            } catch {
                omittedRecordCount += 1
            }
        }

        return { dailyMessages, metadata, omittedRecordCount }
    }

    public async replace(
        dailyMessages: readonly DailyMessages[],
        metadata: readonly MessagingMetadata[],
        cutoffDate: string
    ): Promise<void> {
        const retainedDailyMessages = dailyMessages.filter(({ date }) => date >= cutoffDate)
        const dailyRecords = retainedDailyMessages.map((value) => ({
            cacheKey: FamilyMessagingStore.#dailyKey(value),
            value: Value.Encode(DailyMessages, value)
        }))
        const metadataRecords = metadata.map((value) => ({
            subjectKey: value.subjectKey,
            value: Value.Encode(MessagingMetadata, value)
        }))

        const [existingDailyRecords, existingMetadataRecords] = await Promise.all([
            this.#dailyCollection.findAll(),
            this.#metadataCollection.findAll()
        ])

        await this.#dailyCollection.bulkPut(dailyRecords)
        await this.#metadataCollection.bulkPut(metadataRecords)

        const dailyKeys = new Set(dailyRecords.map(({ cacheKey }) => cacheKey))
        const metadataKeys = new Set(metadataRecords.map(({ subjectKey }) => subjectKey))
        await Promise.all([
            ...existingDailyRecords
                .filter(({ cacheKey }) => !dailyKeys.has(cacheKey))
                .map(async ({ cacheKey }) => await this.#dailyCollection.remove(cacheKey)),
            ...existingMetadataRecords
                .filter(({ subjectKey }) => !metadataKeys.has(subjectKey))
                .map(async ({ subjectKey }) => await this.#metadataCollection.remove(subjectKey))
        ])
    }

    static #dailyKey(value: DailyMessages): string {
        return `${value.subjectKey}:${value.date}`
    }
}
